import { z } from 'zod'
import { ApiError, type ApiClient, type HttpMethod } from './client'

/**
 * Case management V2 — the FastAPI case-management service, mounted at
 * `/{clusterId}/cm`. Contract read from the service's routers and schemas
 * (case_mgmt_service, branch dev-case-mgmt-v2-colocate, 2026-10-13); its
 * swagger.json is stale and was not used.
 *
 *   GET    /cases                        paged list: status[] · category[] · origin · legacy_case_id
 *   GET    /cases/{id}/workspace         case + counts + signals / entities sections
 *   POST   /cases                        create (starts NEW)
 *   PATCH  /cases/{id}                   title · description · severity · owner · assignee · resolution
 *   POST   /cases/{id}/transitions       change status (reason · note · resolution fields)
 *   POST   /cases/bulk/transition        close up to 50 cases, each with its disposition; per-case results
 *   PUT    /cases/{id}/disposition       edit a closed case's disposition (Epic 20)
 *   POST   /cases/{id}/signals           attach ONE signal (snapshot taken server-side)
 *   POST   /cases/export                 export JSON for up to 50 cases
 *   GET    /statuses                     the status catalogue with flags and allowed_next
 *   GET    /cases/{id}/timeline          merged activity feed, newest first (kinds · cursor)
 *   GET    /cases/{id}/audit-log         the audit trail with each action's detail
 *   GET    /cases/{id}/notes, POST       notes (add only; no edit or delete)
 *   GET    /itsm/links/{c}/{w}/sync-state  a case's ticket on one connection (404 = none)
 *   POST   /itsm/sync                    create or update a case's ticket
 *   GET    /cases/{id}/evidence          evidence files (POST upload, PATCH description,
 *                                        GET …/download; DELETE — the console's only delete)
 *   GET    /evidence/usage               the tenant's evidence limits and use
 *   GET    /cases/{id}/relations         related cases (POST links one — UUID or case-N; dismiss is not used)
 *   DELETE /cases/{id}/relations/{rel}   unlink: a manual link is deleted, an automatic one dismissed
 *   GET    /cases/{id}/relations/candidates  cases that can be linked
 *   GET    /cases/{id}/cluster           cases reachable through relations, 1–3 hops
 *   GET    /cases/closed                 the scope's closed cases (closed_at, disposition), newest
 *                                        closure first — the closure trend (Epic 20)
 *   GET    /stats/case-metrics           MTTA / MTTR over a window, grouped by status category ·
 *                                        severity · origin · assignee — also the case counts; MTTD
 *                                        scope-wide over signals (mean only, no groups)
 *
 * Every call carries `scope_id` — in the query, and in the body of JSON writes
 * (`createCmCall`) — next to the SSID header. Errors are
 * FastAPI `detail` bodies; the HTTP core folds them into ApiError.message.
 * Not in the service: all-time per-category counts (the console reads them
 * per window from case-metrics' groups), a users list (the console's
 * users/list).
 */

/**
 * A case number as the service shows it and its case paths accept: `case-23` —
 * lowercase, no leading zero, at most 18 digits, no normalisation (`CASE-23`
 * and `case-023` are not numbers; a legacy id may look like them).
 */
export const CM_CASE_NUMBER = /^case-[1-9][0-9]{0,17}$/
export const CM_SEVERITIES = ['critical', 'high', 'medium', 'low', 'informational'] as const
export type CmSeverity = (typeof CM_SEVERITIES)[number]
/** The service's status categories (Epic 18: the initial one is `new`; `open` is refused with 422). */
export const CM_CATEGORIES = ['new', 'in_progress', 'on_hold', 'closed'] as const
export type CmCategory = (typeof CM_CATEGORIES)[number]
export const CM_ORIGINS = ['manual', 'automated'] as const
export type CmOrigin = (typeof CM_ORIGINS)[number]

/** Bulk close takes at most this many distinct ids. */
export const CM_BULK_CLOSE_MAX = 50

/** How a closed case ended (Epic 20): required on every close; null on cases not closed or closed before it. */
export const CM_DISPOSITIONS = ['FALSE_POSITIVE', 'TRUE_POSITIVE', 'TRUE_NEGATIVE', 'OTHER'] as const
export type CmDisposition = (typeof CM_DISPOSITIONS)[number]

/** An ISO instant → epoch ms; null when absent or unparseable. */
export const cmMs = z
  .string()
  .nullable()
  .optional()
  .transform((v) => {
    if (!v) return null
    const t = Date.parse(v)
    return Number.isFinite(t) ? t : null
  })
const optText = z.string().nullable().optional().transform((v) => v ?? null)

export const cmCase = z
  .object({
    id: z.string(),
    /** `case-N` — the number people use for the case. Absent before service 92dc4869. */
    case_number: optText,
    title: z.string(),
    description: optText,
    severity: z.enum(CM_SEVERITIES),
    status: z.string(),
    status_category: z.enum(CM_CATEGORIES).nullable().optional(),
    owner: optText,
    assignee: optText,
    origin: z.enum(CM_ORIGINS).default('manual'),
    created_at: cmMs,
    updated_at: cmMs,
    closed_at: cmMs,
    acknowledged_at: cmMs,
    tta_seconds: z.number().nullable().optional(),
    legacy_case_id: optText,
    resolution_reason: optText,
    root_cause: optText,
    disposition: optText,
  })
  .transform((c) => ({
    id: c.id,
    caseNumber: c.case_number,
    title: c.title,
    description: c.description,
    severity: c.severity,
    status: c.status,
    /** null for an unknown or archived status. */
    category: c.status_category ?? null,
    owner: c.owner,
    assignee: c.assignee,
    origin: c.origin,
    createdAt: c.created_at,
    updatedAt: c.updated_at,
    closedAt: c.closed_at,
    acknowledgedAt: c.acknowledged_at,
    ttaSeconds: c.tta_seconds ?? null,
    legacyId: c.legacy_case_id,
    resolutionReason: c.resolution_reason,
    rootCause: c.root_cause,
    /** Set while closed; reopening clears it. A stored value this client does not know reads as is. */
    disposition: c.disposition,
  }))
export type CmCase = z.output<typeof cmCase>

const page = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), total: z.number(), page: z.number(), page_size: z.number() }).transform((p) => ({
    items: p.items as z.output<T>[],
    total: p.total,
    page: p.page,
    pageSize: p.page_size,
  }))
export const cmCasePage = page(cmCase)
export type CmPage<T> = { items: T[]; total: number; page: number; pageSize: number }

const nullable = <T extends z.ZodTypeAny>(t: T) => t.nullable().optional().transform((v) => (v ?? null) as z.output<T> | null)

export const cmSignalLink = z
  .object({
    id: z.string(),
    signal_id: z.string(),
    snapshot: z
      .object({
        title: nullable(z.string()),
        severity: nullable(z.string()),
        score: nullable(z.number()),
        first_seen: cmMs,
        last_seen: cmMs,
        false_positive: nullable(z.boolean()),
      })
      .passthrough()
      .default({}),
    note: optText,
    attached_by: z.string(),
    attached_at: cmMs,
  })
  .transform((l) => ({
    id: l.id,
    signalId: l.signal_id,
    title: l.snapshot.title,
    severity: l.snapshot.severity,
    score: l.snapshot.score,
    firstSeen: l.snapshot.first_seen,
    lastSeen: l.snapshot.last_seen,
    falsePositive: l.snapshot.false_positive === true,
    note: l.note,
    attachedBy: l.attached_by,
    attachedAt: l.attached_at,
  }))
export type CmSignalLink = z.output<typeof cmSignalLink>

export const cmEntityLink = z
  .object({
    id: z.string(),
    entity_id: z.string(),
    snapshot: z
      .object({ name: nullable(z.string()), entity_type: nullable(z.string()), source: nullable(z.string()) })
      .passthrough()
      .default({}),
    note: optText,
    attached_by: z.string(),
    attached_at: cmMs,
  })
  .transform((l) => ({
    id: l.id,
    entityId: l.entity_id,
    name: l.snapshot.name ?? l.entity_id,
    type: l.snapshot.entity_type,
    source: l.snapshot.source,
    note: l.note,
    attachedBy: l.attached_by,
    attachedAt: l.attached_at,
  }))
export type CmEntityLink = z.output<typeof cmEntityLink>

const section = <T extends z.ZodTypeAny>(item: T) =>
  z
    .object({ items: z.array(item), total: z.number(), truncated: z.boolean().default(false) })
    .optional()
    .transform((s) => (s ? { items: s.items as z.output<T>[], total: s.total, truncated: s.truncated } : null))

export const cmWorkspace = z
  .object({
    case: cmCase,
    counts: z.object({
      signals: z.number(),
      entities: z.number(),
      notes: z.number(),
      relations_active: z.number(),
      audit_entries: z.number().default(0),
      evidence_files: z.number().default(0),
      evidence_bytes: z.number().default(0),
    }),
    signals: section(cmSignalLink),
    entities: section(cmEntityLink),
  })
  .transform((w) => ({
    case: w.case,
    counts: {
      signals: w.counts.signals,
      entities: w.counts.entities,
      notes: w.counts.notes,
      relations: w.counts.relations_active,
      evidence: w.counts.evidence_files,
    },
    signals: w.signals,
    entities: w.entities,
  }))
export type CmWorkspace = z.output<typeof cmWorkspace>

export const cmStatus = z
  .object({
    key: z.string(),
    label: z.string(),
    category: z.enum(CM_CATEGORIES),
    is_builtin: z.boolean().default(false),
    archived: z.boolean().default(false),
    sort_order: z.number().default(0),
    flags: z.record(z.string(), z.boolean()).default({}),
    allowed_next: z.array(z.string()).default([]),
    /** Set only by archive: the cases still in the status. */
    cases_in_status: z.number().nullable().optional(),
  })
  .transform((s) => ({
    key: s.key,
    label: s.label,
    category: s.category,
    builtin: s.is_builtin,
    archived: s.archived,
    order: s.sort_order,
    requireReason: s.flags.require_reason === true,
    requireAssignee: s.flags.require_assignee === true,
    requireNote: s.flags.require_note === true,
    requireResolutionReason: s.flags.require_resolution_reason === true,
    requireRootCause: s.flags.require_root_cause === true,
    allowedNext: s.allowed_next,
    casesInStatus: s.cases_in_status ?? null,
  }))
export type CmStatus = z.output<typeof cmStatus>
export const cmStatuses = z.array(cmStatus)

/**
 * What an ITSM status mapping saves for the New status. The ITSM side — the
 * field mapper, the manifest, the DNIF status scale — still names it `OPEN`;
 * the service turns a case's `NEW` into `OPEN` before it looks the mapping
 * up, so a mapping saved as `NEW` never matches. Every mapper saves this.
 */
export const CM_ITSM_NEW_STATUS = 'OPEN'

/** A status key as an ITSM mapping value: `NEW` → `OPEN`, every other key as is. */
export const cmItsmStatusValue = (key: string) => (key === 'NEW' ? CM_ITSM_NEW_STATUS : key)

/**
 * The four built-in statuses as an ITSM status mapping offers them, in
 * order — mapping value and label, New as `OPEN`. The one list both mappers
 * (Plugins and Case settings › ITSM) start from.
 */
export const CM_ITSM_BUILTIN_STATUSES: readonly { value: string; label: string }[] = [
  { value: CM_ITSM_NEW_STATUS, label: 'New' },
  { value: 'IN-PROGRESS', label: 'In progress' },
  { value: 'ON-HOLD', label: 'On hold' },
  { value: 'CLOSED', label: 'Closed' },
]

export const cmBulkResult = z
  .object({
    results: z.array(
      z.object({ case_id: z.string(), ok: z.boolean(), unchanged: z.boolean().default(false), error_code: z.string().nullable().optional() }),
    ),
    summary: z.object({ total: z.number(), succeeded: z.number(), unchanged: z.number(), failed: z.number() }),
  })
  .transform((r) => ({
    results: r.results.map((x) => ({ caseId: x.case_id, ok: x.ok, unchanged: x.unchanged, errorCode: x.error_code ?? null })),
    summary: r.summary,
  }))
export type CmBulkResult = z.output<typeof cmBulkResult>

const metricStats = z
  .object({
    count: z.number().default(0),
    mean_seconds: z.number().nullable().optional(),
    median_seconds: z.number().nullable().optional(),
    p90_seconds: z.number().nullable().optional(),
    excluded: z.record(z.string(), z.number()).default({}),
  })
  .transform((m) => ({
    count: m.count,
    mean: m.mean_seconds ?? null,
    median: m.median_seconds ?? null,
    p90: m.p90_seconds ?? null,
    excluded: m.excluded,
  }))
export type CmMetric = z.output<typeof metricStats>

/** The breakdown dimensions `group_by` takes; they apply to MTTA and MTTR. */
export const CM_METRIC_DIMS = ['status_category', 'severity', 'origin', 'assignee', 'disposition'] as const
export type CmMetricDim = (typeof CM_METRIC_DIMS)[number]

/** One group of a breakdown; `key` null = unassigned (assignee), not set (disposition). */
const metricGroup = z.object({ key: z.string().nullable(), stats: metricStats })
export type CmMetricGroup = z.output<typeof metricGroup>

const metricResult = z
  .object({ total: metricStats, groups: z.record(z.string(), z.array(metricGroup)).nullable().optional() })
  .passthrough()
  .transform((m) => ({ total: m.total, groups: (m.groups ?? {}) as Partial<Record<CmMetricDim, CmMetricGroup[]>> }))

/**
 * `GET /stats/case-metrics`. MTTA covers the cases CREATED in the window, so
 * a group's `count + Σ excluded` is how many cases created in the window sit
 * in that group now — the source of every case count the console shows.
 * MTTR covers the cases CLOSED in the window. MTTD is scope-wide over the
 * signals in the window: a mean only (median and p90 null), never grouped.
 */
export const cmCaseMetrics = z
  .object({ metrics: z.record(z.string(), metricResult) })
  .transform((r) => ({
    mtta: r.metrics.mtta?.total ?? null,
    mttr: r.metrics.mttr?.total ?? null,
    mttd: r.metrics.mttd?.total ?? null,
    groups: { mtta: r.metrics.mtta?.groups ?? {}, mttr: r.metrics.mttr?.groups ?? {} },
  }))
export type CmCaseMetrics = z.output<typeof cmCaseMetrics>

/** The trailing windows `GET /cases/closed` takes as `window` (ending now, UTC). */
export const CM_CLOSED_WINDOWS = ['7d', '30d', '90d', '180d', '365d', '730d'] as const
export type CmClosedWindow = (typeof CM_CLOSED_WINDOWS)[number]
/** `GET /cases/closed` pages at most this many rows. */
export const CM_CLOSED_PAGE_MAX = 200

/** One row of `GET /cases/closed`; disposition null = closed before dispositions existed, or by the system. */
export const cmClosedCase = z
  .object({ case_id: z.string(), case_number: optText, name: z.string(), disposition: optText, closed_at: cmMs })
  .transform((c) => ({ caseId: c.case_id, caseNumber: c.case_number, name: c.name, disposition: c.disposition, closedAt: c.closed_at }))
export type CmClosedCase = z.output<typeof cmClosedCase>
export const cmClosedPage = page(cmClosedCase)

/** `GET /cases/closed`: a trailing `window`, or closed_at bounds (inclusive) — never both. */
export type CmClosedQuery = ({ window: CmClosedWindow } | { from?: number; to?: number }) & { page: number; pageSize: number }

/** Cases a metric looked at: measured plus every excluded reason. */
export const cmMetricCases = (m: CmMetric | null | undefined) =>
  m ? m.count + Object.values(m.excluded).reduce((a, b) => a + b, 0) : 0

/* --------------------------------------------------------- case activity */

/** The service's timeline kinds; itsm, sla, assignment and system are audit-backed (each carries its `action`). */
export const CM_TIMELINE_KINDS = ['signal', 'note', 'transition', 'audit', 'relation', 'evidence', 'itsm', 'sla', 'assignment', 'system'] as const
export type CmTimelineKind = (typeof CM_TIMELINE_KINDS)[number]
/** An event's kind as read: a known one, or `other` for a kind this client does not know yet (`rawKind` keeps it). */
export type CmTimelineEventKind = CmTimelineKind | 'other'
const knownKind = (k: string): CmTimelineEventKind => ((CM_TIMELINE_KINDS as readonly string[]).includes(k) ? (k as CmTimelineKind) : 'other')

const optStrNull = z.string().nullable().optional().transform((v) => v ?? null)

/**
 * One event of `GET /cases/{id}/timeline`; `kind` says which fields are present.
 * A kind the client does not know reads as `other` — one new kind on the
 * service never fails the whole page.
 */
export const cmTimelineEvent = z
  .object({
    kind: z.string(),
    at: cmMs,
    id: z.string(),
    actor: optStrNull,
    signal_id: optStrNull,
    text: optStrNull,
    from_status: optStrNull,
    to_status: optStrNull,
    action: optStrNull,
    relation_type: optStrNull,
    event: optStrNull,
    other_case_id: optStrNull,
    evidence_id: optStrNull,
    file_name: optStrNull,
    size_bytes: z.number().nullable().optional().transform((v) => v ?? null),
    sha256: optStrNull,
    disposition: optStrNull,
  })
  .transform((e) => ({
    kind: knownKind(e.kind),
    rawKind: e.kind,
    at: e.at,
    id: e.id,
    actor: e.actor,
    signalId: e.signal_id,
    text: e.text,
    fromStatus: e.from_status,
    toStatus: e.to_status,
    action: e.action,
    relationType: e.relation_type,
    event: e.event,
    otherCaseId: e.other_case_id,
    evidenceId: e.evidence_id,
    fileName: e.file_name,
    sizeBytes: e.size_bytes,
    sha256: e.sha256,
    /** A transition into a closed status: the disposition it was closed with. */
    disposition: e.disposition,
  }))
export type CmTimelineEvent = z.output<typeof cmTimelineEvent>

export const cmTimelinePage = z
  .object({ items: z.array(cmTimelineEvent), next_cursor: z.string().nullable().optional() })
  .transform((p) => ({ items: p.items, nextCursor: p.next_cursor ?? null }))
export type CmTimelinePage = z.output<typeof cmTimelinePage>

/** One entry of `GET /cases/{id}/audit-log` (oldest first): the action and its detail. */
export const cmAuditEntry = z
  .object({
    id: z.string(),
    actor: z.string(),
    action: z.string(),
    target_type: z.string().nullable().optional(),
    detail: z.record(z.string(), z.unknown()).nullable().optional(),
    created_at: cmMs,
  })
  .transform((a) => ({ id: a.id, actor: a.actor, action: a.action, targetType: a.target_type ?? null, detail: a.detail ?? {}, createdAt: a.created_at }))
export type CmAuditEntry = z.output<typeof cmAuditEntry>

export const cmAuditPage = z
  .object({ items: z.array(cmAuditEntry), total: z.number(), page: z.number(), page_size: z.number(), has_more: z.boolean().default(false) })
  .transform((p) => ({ items: p.items, total: p.total, page: p.page, pageSize: p.page_size, hasMore: p.has_more }))
export type CmAuditPage = z.output<typeof cmAuditPage>

/** A note. `author` is the writer's login id (the session subject), not a display name. */
export const cmNote = z
  .object({ id: z.string(), case_id: z.string(), author: z.string(), body: z.string(), created_at: cmMs })
  .transform((n) => ({ id: n.id, caseId: n.case_id, author: n.author, body: n.body, createdAt: n.created_at }))
export type CmNote = z.output<typeof cmNote>

/* ---------------------------------------------------------------- evidence */

/** What the service decided a file is, by its content. */
export const CM_EVIDENCE_TYPES = ['pdf', 'csv', 'json'] as const

/**
 * The console's description limit: the service takes 500 characters at upload
 * and 2000 on an edit; the console holds both to 500 (CB-81).
 */
export const CM_EVIDENCE_DESCRIPTION_MAX = 500

/** One evidence file, or (state `deleted`) its tombstone. Content facts ride in `flags`. */
export const cmEvidence = z
  .object({
    id: z.string(),
    case_id: z.string(),
    name: z.string(),
    size: z.number(),
    sniffed_type: z.string().optional(),
    type: z.string().optional(),
    sha256: z.string(),
    flags: z.record(z.string(), z.unknown()).nullable().optional(),
    uploader: z.string(),
    uploaded_at: cmMs,
    description: optStrNull,
    state: z.enum(['uploaded', 'deleted']).default('uploaded'),
    deleted_at: cmMs,
    deleted_by: optStrNull,
  })
  .transform((e) => ({
    id: e.id,
    caseId: e.case_id,
    name: e.name,
    size: e.size,
    type: (e.sniffed_type ?? e.type ?? '').toLowerCase(),
    sha256: e.sha256,
    flags: e.flags ?? {},
    uploader: e.uploader,
    uploadedAt: e.uploaded_at,
    description: e.description,
    state: e.state,
    deletedAt: e.deleted_at,
    deletedBy: e.deleted_by,
  }))
export type CmEvidence = z.output<typeof cmEvidence>

export const cmEvidencePage = z
  .object({ items: z.array(cmEvidence), total: z.number(), page: z.number(), page_size: z.number(), has_more: z.boolean().default(false) })
  .transform((p) => ({ items: p.items, total: p.total, hasMore: p.has_more }))
export type CmEvidencePage = z.output<typeof cmEvidencePage>

/** The tenant's evidence limits and use (`GET /evidence/usage`). */
export const cmEvidenceUsage = z
  .object({
    quota_bytes: z.number(),
    used_bytes: z.number(),
    remaining_bytes: z.number(),
    per_file_max_bytes: z.number(),
    max_files_per_case: z.number(),
    file_count: z.number().optional(),
  })
  .passthrough()
  .transform((u) => ({
    quotaBytes: u.quota_bytes,
    usedBytes: u.used_bytes,
    remainingBytes: u.remaining_bytes,
    perFileMaxBytes: u.per_file_max_bytes,
    maxFilesPerCase: u.max_files_per_case,
    fileCount: u.file_count ?? null,
  }))
export type CmEvidenceUsage = z.output<typeof cmEvidenceUsage>

/** The service caps a note at this many characters. */
export const CM_NOTE_MAX = 10_000

/** A case's ticket on one ITSM connection (`GET /itsm/links/{case}/{config}/sync-state`). */
export const cmSyncState = z
  .object({
    ticket_id: optStrNull,
    synced_at: cmMs,
    last_success_at: cmMs,
    last_error: optStrNull,
    last_error_at: cmMs,
    failed_count: z.number().default(0),
    pending_count: z.number().default(0),
    in_flight_count: z.number().default(0),
  })
  .transform((x) => ({
    ticketId: x.ticket_id,
    syncedAt: x.synced_at,
    lastSuccessAt: x.last_success_at,
    lastError: x.last_error,
    lastErrorAt: x.last_error_at,
    /** Updates that failed for good (parked); an admin can retry them from the outbox. */
    failed: x.failed_count,
    pending: x.pending_count,
    inFlight: x.in_flight_count,
  }))
export type CmSyncState = z.output<typeof cmSyncState>

/** `POST /cases/export`: at most this many distinct cases per request. */
export const CM_EXPORT_MAX = 50

/**
 * `POST /cases/export` — per case, the legacy export's fields (name,
 * severity, status, tta / ttr in seconds, handler, notes oldest first,
 * signal count, score, artifact counts by entity type) plus the case number.
 * Cases come back in request order.
 */
export const cmCaseExport = z
  .object({
    cases: z.array(
      z.object({
        case_id: z.string(),
        case_number: optText,
        case_name: z.string(),
        severity: z.string(),
        status: z.string(),
        tta: z.number().nullable().optional(),
        ttr: z.number().nullable().optional(),
        handler: optText,
        case_data: z.array(z.object({ notes: z.string(), author: z.string(), cnam_time: cmMs })).default([]),
        signals: z.number().default(0),
        score: z.number().nullable().optional(),
        artifacts: z.record(z.string(), z.number()).nullable().optional(),
        disposition: optText,
      }),
    ),
    count: z.number().optional(),
    generated_at: cmMs,
  })
  .transform((r) => ({
    cases: r.cases.map((c) => ({
      caseId: c.case_id,
      caseNumber: c.case_number,
      name: c.case_name,
      severity: c.severity,
      status: c.status,
      ttaSeconds: c.tta ?? null,
      ttrSeconds: c.ttr ?? null,
      handler: c.handler,
      notes: c.case_data.map((n) => ({ text: n.notes, author: n.author, at: n.cnam_time })),
      signals: c.signals,
      score: c.score ?? null,
      artifacts: c.artifacts ?? null,
      disposition: c.disposition,
    })),
    generatedAt: r.generated_at,
  }))
export type CmCaseExport = z.output<typeof cmCaseExport>

/** `POST /itsm/sync`: per (case, connection) — create the ticket, or update it. */
export const cmSyncResult = z
  .object({
    results: z.array(
      z.object({
        case_id: z.string(),
        config_id: z.string(),
        ok: z.boolean(),
        ticket_key: optStrNull,
        created: z.boolean().default(false),
        error: optStrNull,
        notes_posted: z.number().default(0),
        /** Counts only; present when a payload was applied (manual sync with extra_payload). */
        extra_applied: z.object({ added: z.number(), overridden: z.number(), deleted: z.number() }).nullable().optional(),
        /** How many payload presets were merged (Epic 19); absent when none were sent. */
        presets_applied: z.number().nullable().optional(),
      }),
    ),
    summary: z.object({ total: z.number(), succeeded: z.number(), failed: z.number() }),
  })
  .transform((r) => ({
    results: r.results.map((x) => ({
      caseId: x.case_id,
      configId: x.config_id,
      ok: x.ok,
      ticketKey: x.ticket_key,
      created: x.created,
      error: x.error,
      notesPosted: x.notes_posted,
      extraApplied: x.extra_applied ?? null,
      presetsApplied: x.presets_applied ?? null,
    })),
    summary: r.summary,
  }))
export type CmSyncResult = z.output<typeof cmSyncResult>

/* --------------------------------------------------------------- relations */

/** One entity two related cases share, with how often it was seen. */
export const cmSharedEntity = z
  .object({ entity_type: z.string(), name: z.string(), count: z.number() })
  .transform((e) => ({ type: e.entity_type, name: e.name, count: e.count }))
export type CmSharedEntity = z.output<typeof cmSharedEntity>

/** The few fields shown for the other end of a relation, or a case to link. */
export const cmRelatedCase = z
  .object({
    id: z.string(),
    case_number: optText,
    title: z.string(),
    status: z.string(),
    status_category: z.enum(CM_CATEGORIES).nullable().optional(),
    severity: z.enum(CM_SEVERITIES),
    origin: z.enum(CM_ORIGINS),
  })
  .transform((c) => ({
    id: c.id,
    caseNumber: c.case_number,
    title: c.title,
    status: c.status,
    category: c.status_category ?? null,
    severity: c.severity,
    origin: c.origin,
  }))
export type CmRelatedCase = z.output<typeof cmRelatedCase>

/** `auto` — the relations engine found shared entities; `manual` — an analyst linked the cases. */
export const CM_RELATION_ORIGINS = ['auto', 'manual'] as const

/** A relation seen from one case. `createdBy` is `system:case-relations` or the linking user's login id. */
export const cmRelation = z
  .object({
    relation_id: z.string(),
    state: z.enum(['active', 'dismissed']),
    origin: z.enum(CM_RELATION_ORIGINS),
    shared_count: z.number(),
    evidence: z.array(cmSharedEntity),
    first_seen: cmMs,
    last_seen: cmMs,
    created_by: z.string(),
    other_case: cmRelatedCase,
  })
  .transform((r) => ({
    id: r.relation_id,
    state: r.state,
    origin: r.origin,
    shared: r.shared_count,
    entities: r.evidence,
    firstSeen: r.first_seen,
    lastSeen: r.last_seen,
    createdBy: r.created_by,
    other: r.other_case,
  }))
export type CmRelation = z.output<typeof cmRelation>
export const cmRelationPage = page(cmRelation)
export const cmCandidatePage = page(cmRelatedCase)

/** The service's hop limit and case cap for a cluster. */
export const CM_CLUSTER_MAX_HOPS = 3
export const CM_CLUSTER_MAX_CASES = 100

/** `GET /cases/{id}/cluster`: the cases reachable through active relations, and the relations among them. */
export const cmCluster = z
  .object({
    nodes: z.array(
      z.object({
        case_id: z.string(),
        case_number: optText,
        title: z.string(),
        status: z.string(),
        severity: z.enum(CM_SEVERITIES),
        origin: z.enum(CM_ORIGINS),
        distance: z.number(),
      }),
    ),
    edges: z.array(
      z.object({
        relation_id: z.string(),
        case_a: z.string(),
        case_b: z.string(),
        shared_count: z.number(),
        origin: z.enum(CM_RELATION_ORIGINS),
        entity_summary: z.array(cmSharedEntity),
      }),
    ),
    truncated: z.boolean(),
  })
  .transform((g) => ({
    nodes: g.nodes.map((n) => ({ id: n.case_id, caseNumber: n.case_number, title: n.title, status: n.status, severity: n.severity, origin: n.origin, distance: n.distance })),
    edges: g.edges.map((e) => ({ id: e.relation_id, a: e.case_a, b: e.case_b, shared: e.shared_count, origin: e.origin, entities: e.entity_summary })),
    truncated: g.truncated,
  }))
export type CmCluster = z.output<typeof cmCluster>
export type CmClusterNode = CmCluster['nodes'][number]
export type CmClusterEdge = CmCluster['edges'][number]

/* ------------------------------------------------------------------ inputs */

export interface CmScope {
  clusterId: string
  scopeId: string
}

export interface CmListQuery {
  page: number
  pageSize: number
  status?: string[]
  category?: CmCategory[]
  origin?: CmOrigin
  /** Created at or after / at or before (ms; both inclusive) — `created_from` / `created_to`. */
  createdFrom?: number
  createdTo?: number
  /** Title substring (any case), or an exact case UUID or `case-N`; 1–100 characters, no control characters. */
  q?: string
  severity?: CmSeverity[]
  /** Assignee names; `CM_UNASSIGNED` matches cases with none. At most 20. */
  assignee?: string[]
  /** Cases closed with one of these (OR); cases without one never match. */
  disposition?: CmDisposition[]
}

/** The `assignee` filter value for "no assignee". */
export const CM_UNASSIGNED = 'unassigned'
/** The service's caps on list filters: `q` length, items in one repeated filter. */
export const CM_SEARCH_MAX = 100
export const CM_LIST_FILTER_MAX = 20

/** Which workspace sections to read, and how many rows of each (the service caps it at 50). */
/** The most items a workspace section returns (`sections_page_size` ≤ 50, else 422). */
export const CM_SECTIONS_PAGE_MAX = 50

export interface CmWorkspaceQuery {
  include?: ('signals' | 'entities')[]
  /** At most CM_SECTIONS_PAGE_MAX. */
  sectionsPageSize?: number
}

export interface CmCreateInput {
  title: string
  severity: CmSeverity
  description?: string
  assignee?: string
}

/** Null or absent = unchanged — the service cannot clear a field. */
export interface CmUpdateInput {
  title?: string
  description?: string
  severity?: CmSeverity
  assignee?: string
  resolutionReason?: string
  rootCause?: string
}

export interface CmTransitionInput {
  target: string
  reason?: string
  note?: string
  resolutionReason?: string
  rootCause?: string
  /** Required into a closed status (else 422 `disposition_required`); sent only when given. */
  disposition?: CmDisposition
}

/** `POST /cases/bulk/transition`: every case with its own disposition. */
export interface CmBulkCloseInput {
  items: { caseId: string; disposition: CmDisposition }[]
  status: string
  reason?: string
}

/** `PUT /cases/{id}/disposition` — the case's new and previous value. */
export const cmDispositionChange = z
  .object({ case_id: z.string(), disposition: z.string(), previous_disposition: z.string().nullable().optional() })
  .transform((r) => ({ caseId: r.case_id, disposition: r.disposition, previous: r.previous_disposition ?? null }))
export type CmDispositionChange = z.output<typeof cmDispositionChange>

export interface CaseMgmtApi {
  listCases(scope: CmScope, q: CmListQuery): Promise<CmPage<CmCase>>
  /** Default: signals and entities, 50 rows each (the case page). */
  workspace(scope: CmScope, caseId: string, q?: CmWorkspaceQuery): Promise<CmWorkspace>
  createCase(scope: CmScope, input: CmCreateInput): Promise<CmCase>
  updateCase(scope: CmScope, caseId: string, input: CmUpdateInput): Promise<CmCase>
  transition(scope: CmScope, caseId: string, input: CmTransitionInput): Promise<void>
  bulkClose(scope: CmScope, input: CmBulkCloseInput): Promise<CmBulkResult>
  /** Edit the disposition of a closed case: 409 when it is not closed, 404 when missing or in another scope. */
  setDisposition(scope: CmScope, caseId: string, disposition: CmDisposition): Promise<CmDispositionChange>
  attachSignal(scope: CmScope, caseId: string, input: { signalId: string; note?: string }): Promise<CmSignalLink>
  /** At most CM_EXPORT_MAX distinct cases; the service answers in request order. */
  exportCases(scope: CmScope, caseIds: string[]): Promise<CmCaseExport>
  statuses(scope: CmScope): Promise<CmStatus[]>
  caseMetrics(scope: CmScope, window: { from: number; to: number; groupBy?: readonly CmMetricDim[] }): Promise<CmCaseMetrics>
  /** Closed cases, newest closure first; 403 for viewers, 422 for a bad window or range. */
  closedCases(scope: CmScope, q: CmClosedQuery): Promise<CmPage<CmClosedCase>>
  /** Newest first; `cursor` from the previous page's `nextCursor`. */
  timeline(scope: CmScope, caseId: string, q: { kinds?: readonly CmTimelineKind[]; pageSize: number; cursor?: string | null }): Promise<CmTimelinePage>
  /** Oldest first, paged; carries each action's detail. */
  auditLog(scope: CmScope, caseId: string, q: { page: number; pageSize: number }): Promise<CmAuditPage>
  /** Every note of the case, oldest first (the service does not page them). */
  notes(scope: CmScope, caseId: string): Promise<CmNote[]>
  addNote(scope: CmScope, caseId: string, body: string): Promise<CmNote>
  /** null: the case has no ticket on that connection (the service answers 404). */
  syncState(scope: CmScope, caseId: string, configId: string): Promise<CmSyncState | null>
  /**
   * `presetIds`: per connection, saved payload presets merged in the listed
   * order, the later winning (Epic 19); `extraPayload`: per connection, JSON
   * merged on top of them (Epic 17). Both for ticket create and update only.
   */
  syncToItsm(
    scope: CmScope,
    input: { caseIds: string[]; configIds: string[]; presetIds?: Record<string, string[]>; extraPayload?: Record<string, Record<string, unknown>> },
  ): Promise<CmSyncResult>
  /** 404 while the tenant's `evidence_attachments` is off (operator-managed). */
  evidence(scope: CmScope, caseId: string, q: { page: number; pageSize: number; sort: 'uploaded_at_desc' | 'size_desc'; includeDeleted: boolean }): Promise<CmEvidencePage>
  evidenceUsage(scope: CmScope): Promise<CmEvidenceUsage>
  /** One file (multipart `file` + optional `description`); the service checks type by content and scans it. */
  uploadEvidence(scope: CmScope, caseId: string, file: File, description?: string): Promise<CmEvidence>
  /** Only the description can change; null clears it. */
  updateEvidence(scope: CmScope, caseId: string, evidenceId: string, description: string | null): Promise<CmEvidence>
  /** The stored bytes (every download is written to the case's audit log). */
  downloadEvidence(scope: CmScope, caseId: string, evidenceId: string): Promise<Blob>
  /**
   * Soft delete to a tombstone — the console's ONLY delete call (user,
   * 2026-10-14). Security Engineer only; the service refuses it while the
   * case is open (409 `case_open`).
   */
  deleteEvidence(scope: CmScope, caseId: string, evidenceId: string): Promise<CmEvidence>
  /** Both directions, newest activity first; dismissed ones only when asked. Readable with relations off. */
  relations(scope: CmScope, caseId: string, q: { page: number; pageSize: number; includeDismissed: boolean }): Promise<CmPage<CmRelation>>
  /** Cases that can be linked: title or id match; never the case itself or one it is already actively related to. */
  relationCandidates(scope: CmScope, caseId: string, q: { q?: string; page: number; pageSize: number }): Promise<CmPage<CmRelatedCase>>
  /**
   * A manual link (403 while the tenant's case relations are off). An active
   * relation comes back unchanged; a dismissed one becomes an active manual one.
   */
  /** `otherCaseId` is the other case's UUID or its `case-N`. */
  linkCase(scope: CmScope, caseId: string, otherCaseId: string): Promise<CmRelation>
  /** A manual relation is deleted; an automatic one is dismissed (the engine never adds it again). 404 when gone. */
  unlinkCase(scope: CmScope, caseId: string, relationId: string): Promise<void>
  /** Breadth first over active relations, `hops` 1–3; `truncated` when the case cap cut it short. */
  cluster(scope: CmScope, caseId: string, q: { hops: number; maxCases?: number }): Promise<CmCluster>
}

/** Legacy ids the service accepts (`case-123`). */
export const CM_LEGACY_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/

const trimmed = (v: string | undefined) => {
  const t = v?.trim()
  return t ? t : undefined
}

export type CmCall = <T>(
  s: CmScope,
  method: HttpMethod,
  path: string,
  opts?: { body?: unknown; schema?: z.ZodType<T, z.ZodTypeDef, unknown>; responseType?: 'json' | 'blob'; upload?: boolean },
) => Promise<T>

/**
 * Every call to the service — cases and settings alike — goes through here:
 * the ONE place the scope is attached. The service reads `scope_id` from the
 * query on reads and body-less calls, and from the JSON body on writes; equal
 * values in both are accepted. So every call carries `?scope_id=`, and a JSON
 * object body carries `scope_id` as well — one rule, no per-route table.
 */
export function createCmCall(client: ApiClient): CmCall {
  return (s, method, path, opts = {}) => {
    const at = path.indexOf('?')
    const query = new URLSearchParams(at < 0 ? '' : path.slice(at + 1))
    query.set('scope_id', s.scopeId)
    const url = `/${encodeURIComponent(s.clusterId)}/cm${at < 0 ? path : path.slice(0, at)}?${query}`
    const body = isJsonObject(opts.body) ? { ...opts.body, scope_id: s.scopeId } : opts.body
    return client.request(method, url, { ...opts, body })
  }
}

/** The service's machine code on an error (`{"detail": {"code", "message"}}`), or null. */
export function cmErrorCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null
  const detail = (e.body as { detail?: unknown } | null)?.detail
  const code = detail && typeof detail === 'object' ? (detail as { code?: unknown }).code : undefined
  return typeof code === 'string' ? code : null
}

/** A plain JSON object body — not a FormData upload, an array or a primitive. */
const isJsonObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype

export function createCaseMgmtApi(client: ApiClient): CaseMgmtApi {
  const enc = encodeURIComponent
  const call = createCmCall(client)

  return {
    listCases: (s, q) => {
      const p = new URLSearchParams({ page: String(q.page), page_size: String(q.pageSize) })
      for (const v of q.status ?? []) p.append('status', v)
      for (const v of q.category ?? []) p.append('category', v)
      if (q.origin) p.set('origin', q.origin)
      if (q.createdFrom !== undefined) p.set('created_from', new Date(q.createdFrom).toISOString())
      if (q.createdTo !== undefined) p.set('created_to', new Date(q.createdTo).toISOString())
      if (q.q?.trim()) p.set('q', q.q.trim())
      for (const v of q.severity ?? []) p.append('severity', v)
      for (const v of q.assignee ?? []) p.append('assignee', v)
      for (const v of q.disposition ?? []) p.append('disposition', v)
      return call(s, 'GET', `/cases?${p}`, { schema: cmCasePage })
    },
    workspace: (s, id, q = {}) => {
      const include = (q.include ?? ['signals', 'entities']).join(',')
      return call(s, 'GET', `/cases/${enc(id)}/workspace?include=${include}&sections_page_size=${q.sectionsPageSize ?? CM_SECTIONS_PAGE_MAX}`, {
        schema: cmWorkspace,
      })
    },
    createCase: (s, i) =>
      call(s, 'POST', `/cases`, {
        body: { title: i.title.trim(), severity: i.severity, description: trimmed(i.description), assignee: trimmed(i.assignee) },
        schema: cmCase,
      }),
    updateCase: (s, id, i) =>
      call(s, 'PATCH', `/cases/${enc(id)}`, {
        body: {
          ...(i.title !== undefined ? { title: i.title.trim() } : {}),
          ...(i.description !== undefined ? { description: i.description } : {}),
          ...(i.severity !== undefined ? { severity: i.severity } : {}),
          ...(trimmed(i.assignee) ? { assignee: trimmed(i.assignee) } : {}),
          ...(trimmed(i.resolutionReason) ? { resolution_reason: trimmed(i.resolutionReason) } : {}),
          ...(trimmed(i.rootCause) ? { root_cause: trimmed(i.rootCause) } : {}),
        },
        schema: cmCase,
      }),
    transition: async (s, id, i) => {
      await call(s, 'POST', `/cases/${enc(id)}/transitions`, {
        body: {
          target_state: i.target,
          reason: trimmed(i.reason) ?? null,
          note: trimmed(i.note) ?? null,
          resolution_reason: trimmed(i.resolutionReason) ?? null,
          root_cause: trimmed(i.rootCause) ?? null,
          ...(i.disposition ? { disposition: i.disposition } : {}),
        },
      })
    },
    bulkClose: (s, i) => {
      // One item per case: the service refuses a repeated case id.
      const seen = new Set<string>()
      const items = i.items.filter((x) => !seen.has(x.caseId) && seen.add(x.caseId))
      return call(s, 'POST', `/cases/bulk/transition`, {
        body: { items: items.map((x) => ({ case_id: x.caseId, disposition: x.disposition })), status: i.status, reason: trimmed(i.reason) ?? null },
        schema: cmBulkResult,
      })
    },
    setDisposition: (s, id, disposition) => call(s, 'PUT', `/cases/${enc(id)}/disposition`, { body: { disposition }, schema: cmDispositionChange }),
    attachSignal: (s, id, i) =>
      call(s, 'POST', `/cases/${enc(id)}/signals`, {
        body: { signal_id: i.signalId, note: trimmed(i.note) ?? null },
        schema: cmSignalLink,
      }),
    exportCases: (s, ids) => call(s, 'POST', `/cases/export`, { body: { case_ids: ids }, schema: cmCaseExport }),
    statuses: (s) => call(s, 'GET', `/statuses`, { schema: cmStatuses }),
    caseMetrics: (s, w) => {
      const p = new URLSearchParams({ from: new Date(w.from).toISOString(), to: new Date(w.to).toISOString(), metrics: 'mtta,mttr,mttd' })
      if (w.groupBy?.length) p.set('group_by', w.groupBy.join(','))
      return call(s, 'GET', `/stats/case-metrics?${p}`, { schema: cmCaseMetrics })
    },
    closedCases: (s, q) => {
      const p = new URLSearchParams()
      if ('window' in q) p.set('window', q.window)
      else {
        if (q.from !== undefined) p.set('closed_from', new Date(q.from).toISOString())
        if (q.to !== undefined) p.set('closed_to', new Date(q.to).toISOString())
      }
      p.set('page', String(q.page))
      p.set('page_size', String(q.pageSize))
      return call(s, 'GET', `/cases/closed?${p}`, { schema: cmClosedPage })
    },
    timeline: (s, id, q) => {
      const p = new URLSearchParams({ page_size: String(q.pageSize) })
      if (q.kinds?.length) p.set('kinds', q.kinds.join(','))
      if (q.cursor) p.set('cursor', q.cursor)
      return call(s, 'GET', `/cases/${enc(id)}/timeline?${p}`, { schema: cmTimelinePage })
    },
    auditLog: (s, id, q) =>
      call(s, 'GET', `/cases/${enc(id)}/audit-log?page=${q.page}&page_size=${q.pageSize}`, { schema: cmAuditPage }),
    notes: async (s, id) => (await call(s, 'GET', `/cases/${enc(id)}/notes?page_size=200`, { schema: z.object({ items: z.array(cmNote) }) })).items,
    addNote: (s, id, body) => call(s, 'POST', `/cases/${enc(id)}/notes`, { body: { body }, schema: cmNote }),
    syncState: async (s, id, configId) => {
      try {
        return await call(s, 'GET', `/itsm/links/${enc(id)}/${enc(configId)}/sync-state`, { schema: cmSyncState })
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
    syncToItsm: (s, i) =>
      call(s, 'POST', `/itsm/sync`, {
        body: {
          case_ids: i.caseIds,
          config_ids: i.configIds,
          ...(i.presetIds && Object.values(i.presetIds).some((ids) => ids.length) ? { preset_ids: i.presetIds } : {}),
          ...(i.extraPayload && Object.keys(i.extraPayload).length ? { extra_payload: i.extraPayload } : {}),
        },
        schema: cmSyncResult,
      }),
    evidence: (s, id, q) => {
      const p = new URLSearchParams({ page: String(q.page), page_size: String(q.pageSize), sort: q.sort })
      if (q.includeDeleted) p.set('include_deleted', 'true')
      return call(s, 'GET', `/cases/${enc(id)}/evidence?${p}`, { schema: cmEvidencePage })
    },
    evidenceUsage: (s) => call(s, 'GET', `/evidence/usage?page_size=1`, { schema: cmEvidenceUsage }),
    uploadEvidence: (s, id, file, description) => {
      const form = new FormData()
      form.append('file', file, file.name)
      if (description?.trim()) form.append('description', description.trim())
      // An upload: only a stalled send times out, not a slow one (client.ts sendUpload).
      return call(s, 'POST', `/cases/${enc(id)}/evidence`, { body: form, schema: cmEvidence, upload: true })
    },
    updateEvidence: (s, id, evidenceId, description) =>
      call(s, 'PATCH', `/cases/${enc(id)}/evidence/${enc(evidenceId)}`, { body: { description }, schema: cmEvidence }),
    downloadEvidence: (s, id, evidenceId) =>
      call<Blob>(s, 'GET', `/cases/${enc(id)}/evidence/${enc(evidenceId)}/download`, { responseType: 'blob' }),
    deleteEvidence: (s, id, evidenceId) => call(s, 'DELETE', `/cases/${enc(id)}/evidence/${enc(evidenceId)}`, { schema: cmEvidence }),
    relations: (s, id, q) => {
      const p = new URLSearchParams({ page: String(q.page), page_size: String(q.pageSize) })
      if (q.includeDismissed) p.set('include_dismissed', 'true')
      return call(s, 'GET', `/cases/${enc(id)}/relations?${p}`, { schema: cmRelationPage })
    },
    relationCandidates: (s, id, q) => {
      const p = new URLSearchParams({ page: String(q.page), page_size: String(q.pageSize) })
      const text = trimmed(q.q)
      if (text) p.set('q', text.slice(0, 100))
      return call(s, 'GET', `/cases/${enc(id)}/relations/candidates?${p}`, { schema: cmCandidatePage })
    },
    linkCase: (s, id, otherCaseId) => call(s, 'POST', `/cases/${enc(id)}/relations`, { body: { other_case_id: otherCaseId }, schema: cmRelation }),
    unlinkCase: async (s, id, relationId) => {
      await call(s, 'DELETE', `/cases/${enc(id)}/relations/${enc(relationId)}`)
    },
    cluster: (s, id, q) => {
      const p = new URLSearchParams({ hops: String(q.hops), max_cases: String(q.maxCases ?? CM_CLUSTER_MAX_CASES) })
      return call(s, 'GET', `/cases/${enc(id)}/cluster?${p}`, { schema: cmCluster })
    },
  }
}
